import { isNil } from 'lodash';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import { DATE_FORMAT_NUMERIC_WITH_TIMESTAMP } from '../../utils/dateUtils';
import {
  DEFAULT_FIELD_TYPE,
  ENTITY_FIELD_STATUS_INWORKS,
  ENTITY_FIELD_STATUS_PUBLISHED,
  ENTITY_RELEASE_TYPE_PILGRIM,
  ENTITY_STATUS_ARCHIVED,
  ENTITY_STATUS_INWORKS,
  ENTITY_STATUS_NONE,
  ENTITY_STATUS_PUBLISHED,
  FIELDS_COMPUTED_FORMULA,
  FIELDS_FIELD_NAME,
  FIELDS_FIELD_TYPE,
  FIELDS_FIELD_TYPE_LABEL,
  FIELDS_IS_COMPUTED,
  FIELDS_IS_EVENT_ENABLED,
  FIELDS_IS_PART_OF_AI_SEARCH,
  FIELDS_IS_USABLE,
  FIELDS_STATUS_LABEL,
  FIELDS_VALIDATE_AGAINST_LABEL,
  HEADER_ITEM_DEFAULT_LABEL,
  RELATIONS_CHILD_ENTITY_NAME,
  RELATIONS_RELATION_TYPE,
  RESOLVED_FIELDS_SOURCE,
  RESOLVED_FIELDS_TARGET,
  TABLE_FIELDS_CONTROL_TYPE,
  TABLE_RELATIONS_CONTROL_TYPE,
  TABLE_RESOLVED_FIELDS_CONTROL_TYPE,
} from '../constants/entity-designer.constants';

export const generateResolvedFields = (fields = []) => {
  const resolvedFields = fields.filter((field) => field.ResolverFor);

  return resolvedFields.map((field) => {
    const resolverField = fields.find((item) => item.FieldName === field.FieldSet);
    return {
      rowId: uuidv4(),
      source: field.ResolverFor,
      target: field.rowId,
      fieldSetId: resolverField?.rowId,
    };
  });
};

export const modifyEntityModel = (EntityModel = {}) => {
  const relatedEntities = EntityModel.EntityInfo.RelatedEntities?.map((field) => ({
    ...field,
    rowId: uuidv4(),
  }));

  const fields = EntityModel.EntityFields?.map((field) => ({
    ...field,
    rowId: uuidv4(),
  }));

  return {
    EntityInfo: {
      ...EntityModel.EntityInfo,
      RelatedEntities: relatedEntities ?? [],
    },
    EntityFields: fields?.reverse() ?? [],
  };
};

export const getStatusFieldLabel = (status) => {
  switch (status) {
    case ENTITY_FIELD_STATUS_PUBLISHED:
      return 'Published';
    case ENTITY_FIELD_STATUS_INWORKS:
      return 'Inworks';
    default: {
      return '';
    }
  }
};

export const getValidateAgainstLabel = ({ field, linkedField }) => {
  if (field?.FieldSet && field?.ResolverFor) {
    return `[${field.FieldSet}] ${linkedField?.ResolverType}.${field.ResolverFor}`;
  } else if (!field?.FieldSet && !field?.ResolverFor) {
    return field?.ResolverType;
  }
  return '';
};

const getFieldTypeLabelText = (fieldType) =>
  !fieldType
    ? ''
    : fieldType.Name === fieldType.UserTypeName
    ? fieldType.Name
    : `${fieldType.Name} (${fieldType.UserTypeName})`;

export const getTypeFieldLabel = ({ typeId, fieldTypes = [] }) => {
  const fieldType = fieldTypes.find((type) => type.Id === typeId);

  return getFieldTypeLabelText(fieldType);
};

export const getFieldTypeDetails = (field, fieldTypes) => {
  const fieldType = fieldTypes.find((fieldType) => fieldType.Id === field.TypeId);

  return {
    TypeName: fieldType?.Name ?? field.TypeId,
    UserTypeName: fieldType?.UserTypeName ?? field.TypeId,
  };
};

const getHasResolvedFields = ({ rowId, resolvedFields = [] }) =>
  rowId && resolvedFields.some((field) => field.fieldSetId === rowId);

const getLinkedField = ({ field, fields }) => {
  if (!field.FieldSet) {
    return;
  }

  return fields.find((linkedField) => field.FieldSet === linkedField.FieldName);
};

export const modifyEntityFields = ({
  fields = [],
  fieldTypes = [],
  resolvedFields = [],
  validationErrors = [],
}) =>
  fields.map((field) => {
    const linkedField = getLinkedField({ field, fields });

    const processedField = Object.keys(field).reduce((acc, item) => {
      if (isNil(field[item])) {
        acc[item] = '';
      } else {
        acc[item] = field[item];
      }
      return acc;
    }, {});

    return {
      ...processedField,
      ...getFieldTypeDetails(processedField, fieldTypes),
      linkedField,
      hasResolvedFields: getHasResolvedFields({ rowId: processedField.rowId, resolvedFields }),
      errors: validationErrors.filter((error) => error.instanceId === processedField.rowId),
      [FIELDS_STATUS_LABEL]: getStatusFieldLabel(processedField.Status),
      [FIELDS_FIELD_TYPE_LABEL]: getTypeFieldLabel({ typeId: processedField.TypeId, fieldTypes }),
      [FIELDS_VALIDATE_AGAINST_LABEL]: getValidateAgainstLabel({
        field: processedField,
        linkedField,
      }),
    };
  });

export const modifyDate = (date) => {
  if (!moment(date).isValid()) {
    return;
  }

  return moment(date).format(DATE_FORMAT_NUMERIC_WITH_TIMESTAMP);
};

export const getEntityStatusLabel = (status) => {
  switch (status) {
    case ENTITY_STATUS_ARCHIVED:
      return 'Archived';
    case ENTITY_STATUS_PUBLISHED:
      return 'Published';
    case ENTITY_STATUS_INWORKS:
      return 'Inworks';
    case ENTITY_STATUS_NONE:
      return 'None';
    default:
      return '';
  }
};

export const modifyEntityCatalogListItems = (items = []) =>
  items.map((item) => ({
    label: item.Label,
    value: item.Label,
    resolverTypeValue: item.ValueField,
  }));

export const modifyEntitiesListItems = (items = []) =>
  items.map((item) => ({
    label: item.SSE_ENTITY_NAME,
    value: item.SSE_ENTITY_NAME,
  }));

export const modifyFieldTypesListItems = (items = []) =>
  items.map((item) => ({
    label: getFieldTypeLabelText(item),
    value: item.Id,
  }));

export const modifyFieldSetListItems = (items = []) =>
  items.map((field) => ({
    value: field.rowId,
    label: `[${field.FieldName}] ${field.ResolverType}.${field.ResolverTypeValueField ?? ''}`,
  }));

export const modifySourceListItems = (items = []) =>
  items.map((item) => ({
    value: item.Name,
    label: `${item.Label} (${item.Name})`,
  }));

export const modifyTargetListItems = (items = []) =>
  items.map((item) => ({
    value: item.rowId,
    label: item.Label || item.FieldName,
    columnName: item.FieldName,
  }));

export const getIsDeleteButtonShown = ({
  isEditMode,
  fieldTableControlType,
  IsComputed,
  IsSystemAdded,
  Status,
  ResolverFor,
  hasResolvedFields,
}) => {
  switch (fieldTableControlType) {
    case TABLE_FIELDS_CONTROL_TYPE:
      if (!isEditMode) {
        return false;
      }
      if (IsComputed) {
        return true;
      }
      return (
        !IsSystemAdded &&
        isEditMode &&
        Status !== ENTITY_FIELD_STATUS_PUBLISHED &&
        !ResolverFor &&
        !hasResolvedFields
      );
    default:
      return isEditMode;
  }
};

export const getDeleteRowParams = ({ fieldTableControlType, rowId, target }) => {
  switch (fieldTableControlType) {
    case TABLE_RESOLVED_FIELDS_CONTROL_TYPE:
      return { rowId, target };
    default:
      return { rowId };
  }
};

export const getInstanceName = (row) => {
  switch (row.fieldTableControlType) {
    case TABLE_FIELDS_CONTROL_TYPE:
      return row.FieldName ?? '';
    case TABLE_RELATIONS_CONTROL_TYPE:
      return row.Id ?? '';
    case TABLE_RESOLVED_FIELDS_CONTROL_TYPE:
      return row.source ?? '';
    default:
      return '';
  }
};

export const getInstanceTypeLabel = (type) => {
  switch (type) {
    case TABLE_FIELDS_CONTROL_TYPE:
      return 'field';
    case TABLE_RELATIONS_CONTROL_TYPE:
      return 'relation';
    case TABLE_RESOLVED_FIELDS_CONTROL_TYPE:
      return 'resolved field';
    default:
      return '';
  }
};

export const getIsPilgrimEntity = (entity) => entity.ReleaseType === ENTITY_RELEASE_TYPE_PILGRIM;

export const getIsCheckboxVisible = ({ row, key }) => {
  switch (key) {
    case FIELDS_IS_USABLE:
      return row.Status === ENTITY_FIELD_STATUS_PUBLISHED;
    case FIELDS_IS_EVENT_ENABLED:
      return false;
    default:
      return true;
  }
};

export const getTooltipText = (key) => {
  switch (key) {
    case FIELDS_IS_COMPUTED:
    case FIELDS_COMPUTED_FORMULA:
      return `To use the Computed Value, you must select the check box and fill the Formula
              with valid SQL syntax using the current Entity Fields and/or static text.`;
    default:
      return null;
  }
};

export const getIsViewOnly = ({ key, row }) => {
  switch (key) {
    case FIELDS_FIELD_NAME:
      return (
        row.IsSystemAdded ||
        row.Status === ENTITY_FIELD_STATUS_PUBLISHED ||
        row.ResolverFor ||
        row.hasResolvedFields
      );
    case FIELDS_FIELD_TYPE:
      return row.IsSystemAdded || row.Status === ENTITY_FIELD_STATUS_PUBLISHED;
    default:
      return false;
  }
};

export const getViewOnlyValue = ({ listItems, row, key }) =>
  listItems.find((item) => item.value === row[key])?.label ?? '';

export const getDefaultTypeId = (fieldTypes = []) => {
  const defaultFieldType = fieldTypes.find((fieldType) => fieldType.DBType === DEFAULT_FIELD_TYPE);

  return defaultFieldType?.Id ?? '';
};

export const getIsRequired = ({ key, row }) => {
  switch (key) {
    case FIELDS_FIELD_NAME:
    case FIELDS_FIELD_TYPE:
      return row.Status !== ENTITY_FIELD_STATUS_PUBLISHED;
    case RELATIONS_CHILD_ENTITY_NAME:
    case RELATIONS_RELATION_TYPE:
    case RESOLVED_FIELDS_SOURCE:
    case RESOLVED_FIELDS_TARGET:
      return true;
    default:
      return false;
  }
};

export const getIsCellDisabled = ({ key, row }) => {
  switch (key) {
    case FIELDS_FIELD_NAME:
      return !row.isEditMode || row.IsSystemAdded || row.Status === ENTITY_FIELD_STATUS_PUBLISHED;
    case FIELDS_COMPUTED_FORMULA:
      return !row.isEditMode || !row.hasSpecializedRight || !row.IsComputed;
    default:
      return !row.isEditMode;
  }
};

export const getRelationTypeLabel = ({ type, types }) =>
  types.find((item) => item.value === type)?.label ?? type;

export const getIsCheckboxDisabled = ({ row, key }) => {
  switch (key) {
    case FIELDS_IS_COMPUTED:
      return (
        !row.isEditMode || !row.hasSpecializedRight || row.Status === ENTITY_FIELD_STATUS_PUBLISHED
      );
    case FIELDS_IS_PART_OF_AI_SEARCH:
      return (
        !row.isEditMode ||
        (row.rows.find((row) => row[FIELDS_IS_PART_OF_AI_SEARCH]) &&
          !row[FIELDS_IS_PART_OF_AI_SEARCH])
      );
    default:
      return !row.isEditMode;
  }
};

export const getReleaseVersion = (version) =>
  version || version === 0 ? version : HEADER_ITEM_DEFAULT_LABEL;
